import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import config from '../config';
import { quotaDismissed } from '../api/actions';

const SALES_EMAIL = 'sales@betterfield.pt';
const TIER_COLORS = { basic: 'default', pro: 'primary', enterprise: 'secondary' };

export default function UpgradeDialog() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open, featureId, cost, balance } = useSelector((state) => state.quota);
  // Read the auth token explicitly from redux so these requests don't silently
  // rely on axios.defaults.headers being set by the main API middleware.
  const token = useSelector((state) => state.session && state.session.current && state.session.current.token);

  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [nextPlan, setNextPlan] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const handleClose = useCallback(() => {
    dispatch(quotaDismissed());
  }, [dispatch]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    Promise.all([
      axios.get(`${config.apiBaseUrl}/usage/subscription`, authConfig),
      axios.get(`${config.apiBaseUrl}/usage/plans`, authConfig),
    ])
      .then(([subRes, plansRes]) => {
        if (cancelled) return;
        const sub = subRes.data;
        const plans = plansRes.data || [];
        const currentIdx = plans.findIndex((p) => p.planId === sub.planId);
        setCurrentPlan(currentIdx >= 0 ? plans[currentIdx] : sub);
        setNextPlan(currentIdx >= 0 && currentIdx < plans.length - 1 ? plans[currentIdx + 1] : null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load plans');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, token]);

  // Integer credits are our canonical unit, but defensively clamp tiny
  // negative values (left over from legacy fractional data) to 0 so the UI
  // never shows things like "-2.1e-15 credits".
  const clampCredits = (value) => {
    if (value == null) return null;
    const n = Math.round(Number(value));
    return Number.isFinite(n) ? Math.max(0, n) : null;
  };
  const displayCost = clampCredits(cost);
  const displayBalance = clampCredits(balance);

  const subject = encodeURIComponent(
    t('credits.upgradeDialog.mailSubject', { plan: nextPlan ? nextPlan.name : '' })
  );
  const body = encodeURIComponent(
    t('credits.upgradeDialog.mailBody', {
      currentPlan: currentPlan ? currentPlan.name : '—',
      nextPlan: nextPlan ? nextPlan.name : '—',
    })
  );
  const mailto = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;

  const renderPlanCard = (plan, isCurrent) => {
    if (!plan) return null;
    return (
      <Card variant={isCurrent ? 'outlined' : 'elevation'} sx={{ flex: 1, minWidth: 220 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{plan.name}</Typography>
            {plan.tier && (
              <Chip
                size="small"
                label={t(`credits.tier.${plan.tier}`, { defaultValue: plan.tier })}
                color={TIER_COLORS[plan.tier] || 'default'}
              />
            )}
            {isCurrent && (
              <Chip size="small" label={t('credits.upgradeDialog.currentPlanChip')} variant="outlined" />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {plan.priceEur != null
              ? t('credits.pricePerMonth', { price: plan.priceEur })
              : t('credits.priceCustom')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('credits.upgradeDialog.totalCreditsLabel', { count: plan.totalCredits })}
          </Typography>
          {plan.unlockedFeatures && plan.unlockedFeatures.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {plan.unlockedFeatures.map((f) => (
                <Chip
                  key={f}
                  label={t(`credits.unlocked.${f}`, { defaultValue: f })}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon color="warning" />
        {t('credits.upgradeDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {featureId
            ? t('credits.upgradeDialog.bodyWithFeature', {
                feature: t(`credits.features.${featureId}`, { defaultValue: featureId }),
                cost: displayCost != null ? displayCost : '—',
                balance: displayBalance != null ? displayBalance : '—',
              })
            : t('credits.upgradeDialog.bodyGeneric')}
        </Alert>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {loadError && (
          <Alert severity="error">{loadError}</Alert>
        )}

        {!loading && !loadError && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {renderPlanCard(currentPlan, true)}
            {nextPlan
              ? renderPlanCard(nextPlan, false)
              : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('credits.upgradeDialog.noNextPlan')}
                  </Typography>
                </Box>
              )}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {t('credits.upgradeDialog.stripeComingSoon')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('credits.upgradeDialog.close')}</Button>
        <Button
          variant="contained"
          color="primary"
          href={mailto}
          disabled={loading}
        >
          {t('credits.upgradeDialog.contactSales')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
